/* Карточка встречи: ход работы, саммери и расшифровка.

   Пока запись обрабатывается, экран сам спрашивает сервер — путь длинный
   (звонок → расшифровка → саммери), и человеку важно видеть, что он идёт.
   Готовое саммери уже лежит в переписке с ботом; здесь оно на случай,
   когда искать в чате долго, плюс кнопки «прислать ещё раз» и «пересобрать». */

import {
  clockLen, humanDate, MEETING_STAGE, meetingBusy, meetingProgress, meetingTitle, platformLabel,
} from "@mera/shared";
import { Button, List, ListButton, ListInput, ListItem } from "konsta/react";
import React, { useEffect, useState } from "react";

import { Ion, IconTile } from "../components/icons.jsx";
import Screen from "../components/Screen.jsx";
import { SkelMeeting } from "../components/skeleton.jsx";
import { Bar, Empty, Hint } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useNavigate, useParams } from "../lib/router.jsx";
import { useFetch } from "../lib/store.jsx";
import { alertDialog, confirmDialog, copyText, haptic } from "../lib/tg.js";

const ITEM_COLORS = {
  primaryTextIos: "text-[color:var(--tg-text)]",
  secondaryTextIos: "text-[color:var(--tg-hint)]",
};

const TILE = { send: "#30b0c7", again: "#ff9500", copy: "#8e8e93", text: "#5856d6" };

/** Заголовок раздела саммери — иконка, слово и дальше пункты */
const Section = ({ icon: Icon, title, color, children }) => (
  <div className="px-4 pt-5">
    <div className="flex items-center gap-2 pb-2">
      <Icon size={16} style={{ color: color || "var(--tg-hint)" }} />
      <span className="text-[13px] font-semibold uppercase tracking-wide" style={{ color: "var(--tg-hint)" }}>
        {title}
      </span>
    </div>
    <div className="card-tg px-4 py-3.5 flex flex-col gap-2">{children}</div>
  </div>
);

const Bullet = ({ children }) => (
  <div className="flex gap-2 text-[14px] leading-snug">
    <span style={{ color: "var(--tg-hint)" }}>•</span>
    <span className="min-w-0">{children}</span>
  </div>
);

const has = (v) => Array.isArray(v) && v.filter(Boolean).length > 0;

export default function MeetingCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: m, loading, error, refetch } = useFetch(`/meetings/${id}`);
  const [openTranscript, setOpenTranscript] = useState(false);
  const [instruction, setInstruction] = useState(null);
  const [busy, setBusy] = useState(false);

  /* Пока идёт работа — переспрашиваем сервер; готовую встречу не трогаем */
  const working = meetingBusy(m);
  useEffect(() => {
    if (!working) return undefined;
    const timer = setInterval(refetch, 5000);
    return () => clearInterval(timer);
  }, [working, refetch]);

  if (loading && !m) return <Screen back title="Встреча"><SkelMeeting /></Screen>;
  if (error || !m) {
    return (
      <Screen back title="Встреча">
        <Empty text={error || "Встреча не найдена"} icon={Ion.warning} />
      </Screen>
    );
  }

  const s = m.summary || {};
  const when = [
    humanDate(m.createdAt),
    clockLen(m.durationSec),
    m.source === "recall" ? platformLabel(m.platform) : m.audioName ? "запись файлом" : null,
  ].filter(Boolean);

  const send = async () => {
    haptic.medium();
    setBusy(true);
    try {
      await api.post(`/meetings/${id}/send`, {});
      haptic.success();
      alertDialog("Отправил — саммери и расшифровка в переписке с ботом.");
    } catch (e) {
      haptic.error();
      alertDialog(e.message);
    } finally {
      setBusy(false);
    }
  };

  const resummarize = async () => {
    haptic.medium();
    setBusy(true);
    try {
      await api.post(`/meetings/${id}/resummarize`, { instruction: instruction ?? m.instruction ?? "" });
      setInstruction(null);
      refetch();
    } catch (e) {
      haptic.error();
      alertDialog(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!(await confirmDialog("Удалить встречу вместе с записью и расшифровкой?"))) return;
    haptic.medium();
    try {
      await api.del(`/meetings/${id}`);
      navigate("/meetings", { replace: true });
    } catch (e) {
      haptic.error();
      alertDialog(e.message);
    }
  };

  const copy = async () => {
    haptic.light();
    const text = [
      meetingTitle(m),
      s.summary,
      ...(s.decisions || []).map((d) => `• ${d}`),
      ...(s.tasks || []).map((t) => `• ${t.what}${t.who && t.who !== "—" ? ` — ${t.who}` : ""}`),
    ]
      .filter(Boolean)
      .join("\n");
    alertDialog((await copyText(text)) ? "Саммери скопировано" : text);
  };

  return (
    <Screen back title={meetingTitle(m)} bottomPad={40}>
      <div className="px-4 pt-1">
        <div className="card-tg px-4 py-3.5">
          <div className="text-[17px] font-semibold leading-snug">{meetingTitle(m)}</div>
          <div className="mt-1 text-[13px]" style={{ color: "var(--tg-hint)" }}>
            {when.join(" · ")}
          </div>
        </div>
      </div>

      {working ? (
        <div className="px-4 pt-4">
          <Bar value={Math.round(meetingProgress(m) * 100)} />
          <div className="pt-2">
            <Hint>{m.stage || MEETING_STAGE[m.status]}</Hint>
          </div>
        </div>
      ) : null}

      {m.status === "error" ? (
        <div className="px-4 pt-4">
          <div className="card-tg px-4 py-3.5 flex gap-2.5">
            <Ion.warning size={20} style={{ color: "var(--fin-red)" }} />
            <div className="text-[14px] leading-snug">{m.error || "Не получилось обработать запись"}</div>
          </div>
        </div>
      ) : null}

      {m.status === "recording" ? (
        <div className="px-4 pt-3">
          <Hint>
            Бот сидит в звонке. Если встреча с комнатой ожидания — впустите его; запись
            заберём сразу, как звонок закончится.
          </Hint>
        </div>
      ) : null}

      {s.summary ? (
        <div className="px-4 pt-4">
          <div className="card-tg px-4 py-3.5 text-[15px] leading-relaxed">{s.summary}</div>
        </div>
      ) : null}

      {has(s.topics) ? (
        <Section icon={Ion.chatbubbles} title="Обсудили">
          {s.topics.map((t, i) => (
            <div key={i} className={i ? "pt-2" : ""}>
              <div className="text-[14px] font-semibold pb-1">{t.title}</div>
              {(t.points || []).map((p, j) => (
                <Bullet key={j}>{p}</Bullet>
              ))}
            </div>
          ))}
        </Section>
      ) : null}

      {has(s.decisions) ? (
        <Section icon={Ion.checkmarkDone} title="Решили" color="var(--fin-green)">
          {s.decisions.map((d, i) => (
            <Bullet key={i}>{d}</Bullet>
          ))}
        </Section>
      ) : null}

      {has(s.tasks) ? (
        <Section icon={Ion.checkbox} title="Задачи" color="var(--tg-button)">
          {s.tasks.map((t, i) => {
            const tail = [t.who, t.when].filter((v) => v && v !== "—").join(", ");
            return (
              <Bullet key={i}>
                {t.what}
                {tail ? (
                  <span style={{ color: "var(--tg-hint)" }}> — {tail}</span>
                ) : null}
              </Bullet>
            );
          })}
        </Section>
      ) : null}

      {has(s.risks) ? (
        <Section icon={Ion.alertCircle} title="Риски и вопросы" color="var(--fin-gold)">
          {s.risks.map((r, i) => (
            <Bullet key={i}>{r}</Bullet>
          ))}
        </Section>
      ) : null}

      {has(s.next) ? (
        <Section icon={Ion.flag} title="Дальше">
          {s.next.map((n, i) => (
            <Bullet key={i}>{n}</Bullet>
          ))}
        </Section>
      ) : null}

      {has(s.participants) ? (
        <div className="px-4 pt-4">
          <Hint>Говорили: {s.participants.join(", ")}</Hint>
        </div>
      ) : null}

      {m.instruction ? (
        <div className="px-4 pt-4">
          <Hint>Просили учесть: {m.instruction}</Hint>
        </div>
      ) : null}

      {m.status === "done" ? (
        <div className="px-4 pt-5">
          <Button large rounded onClick={send} disabled={busy}>
            {busy ? "Отправляю…" : "Прислать в Telegram"}
          </Button>
        </div>
      ) : null}

      {m.transcript ? (
        <>
          <List strong inset>
            <ListItem
              link
              chevron={false}
              colors={ITEM_COLORS}
              media={<IconTile icon={Ion.documentTextSolid} bg={TILE.text} />}
              title="Расшифровка"
              after={openTranscript ? "скрыть" : "показать"}
              onClick={() => {
                haptic.light();
                setOpenTranscript((v) => !v);
              }}
            />
            <ListItem
              link
              chevron={false}
              colors={ITEM_COLORS}
              media={<IconTile icon={Ion.copy} bg={TILE.copy} />}
              title="Скопировать саммери"
              onClick={copy}
            />
          </List>

          {openTranscript ? (
            <div className="px-4 pt-1">
              <div className="card-tg px-4 py-3.5 flex flex-col gap-3">
                {(m.segments || []).length ? (
                  m.segments.map((seg, i) => (
                    <div key={i}>
                      <div className="text-[12px] font-semibold pb-0.5" style={{ color: "var(--tg-hint)" }}>
                        {seg.speaker ? `Спикер ${seg.speaker}` : "Речь"}
                      </div>
                      <div className="text-[14px] leading-snug">{seg.text}</div>
                    </div>
                  ))
                ) : (
                  <div className="text-[14px] leading-snug whitespace-pre-wrap">{m.transcript}</div>
                )}
              </div>
            </div>
          ) : null}

          {/* Саммери можно пересобрать под другой запрос — расшифровка уже есть,
              второй раз её не считаем */}
          <List strong inset>
            <ListInput
              type="textarea"
              inputClassName="!h-20 resize-none"
              placeholder="Что учесть на этот раз"
              value={instruction ?? m.instruction ?? ""}
              onChange={(e) => setInstruction(e.target.value)}
            />
          </List>
          <div className="px-4">
            <Button large rounded onClick={resummarize} disabled={busy || working}>
              Пересобрать
            </Button>
          </div>
        </>
      ) : null}

      <List strong inset>
        <ListButton colors={{ textIos: "text-[color:var(--fin-red)]" }} onClick={remove}>
          Удалить
        </ListButton>
      </List>
    </Screen>
  );
}
